if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product') || document;
      this.productForm = this.productSection.querySelector('product-form');
      this.mainBtn = this.productForm && this.productForm.querySelector('[name="add"]');
      this.btn = this.querySelector('[data-sticky-btn]');
      this.btnLabel = this.querySelector('[data-sticky-btn-label]');
      this.quickBtn = this.querySelector('[data-sticky-quick]');
      this.imgEl = this.querySelector('.sticky-atc__media img');
      this.handleScroll = StickyAtcPanel.handleScroll;

      this.bindEvents();
      this.initMagicButton();
      this.initQuantity();
    }

    /**
     * Razorpay Magic Checkout renders its button in a closed shadow DOM, so it cannot be
     * clicked from outside. Instead the real button is moved into the sticky bar on mobile
     * (and put back on large screens).
     */
    initMagicButton() {
      this.magicSlot = this.querySelector('[data-sticky-magic-slot]');
      if (!this.magicSlot) return;

      const findMagic = () => {
        const host = Array.from(document.querySelectorAll('.magic-buy-btn__parent, magic-checkout-btn')).find(
          (el) => !this.contains(el)
        );
        if (!host) return null;
        return host.closest('.magic-buy-btn__parent') || host;
      };

      this.applyMagic = () => {
        const el = this.magicEl;
        if (!el) return;
        if (this.mobileQuery.matches) {
          if (el.parentNode !== this.magicSlot) {
            this.magicHome = { parent: el.parentNode, next: el.nextSibling };
            this.magicSlot.appendChild(el);
          }
          this.classList.add('sticky-atc-panel--magic');
        } else {
          if (this.magicHome && el.parentNode === this.magicSlot) {
            const { parent, next } = this.magicHome;
            if (next && next.parentNode === parent) parent.insertBefore(el, next);
            else parent.appendChild(el);
          }
          this.classList.remove('sticky-atc-panel--magic');
        }
      };

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.applyMagic);
      }

      const tryAdopt = () => {
        const el = findMagic();
        if (!el) return false;
        this.magicEl = el;
        this.applyMagic();
        return true;
      };

      // The app may inject its button after this script runs, so watch for a few seconds.
      if (!tryAdopt() && 'MutationObserver' in window) {
        this.magicObserver = new MutationObserver(() => {
          if (tryAdopt()) this.magicObserver.disconnect();
        });
        this.magicObserver.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => this.magicObserver && this.magicObserver.disconnect(), 8000);
      }
    }

    /**
     * Mobile: the on-page "quantity + Add to cart" row is hidden, so the quantity selector
     * is moved above the Size options (and stays linked to the product form).
     * Large screens: it is put back in the original row.
     */
    initQuantity() {
      const row = this.productSection.querySelector('.product-info__add-to-cart--sticky-only');
      const qtyEl = row && row.querySelector('quantity-input');
      if (!qtyEl) return;

      const input = qtyEl.querySelector('.qty-input__input');
      const home = { parent: qtyEl.parentNode, next: qtyEl.nextSibling };
      let wrap = null;
      let hidden = null;

      // Copies the visible quantity into a hidden "quantity" field that lives inside the
      // product form, so the cart request always carries the chosen amount.
      this.syncQuantity = () => {
        if (!hidden || !input) return;
        const qty = parseInt(input.value, 10);
        hidden.value = qty > 0 ? qty : 1;
      };

      const restore = () => {
        row.classList.remove('is-qty-fallback');
        if (wrap && qtyEl.parentNode !== home.parent) {
          if (home.next && home.next.parentNode === home.parent) home.parent.insertBefore(qtyEl, home.next);
          else home.parent.appendChild(qtyEl);
        }
        if (wrap) wrap.remove();
        wrap = null;
        if (hidden) hidden.remove();
        hidden = null;
        if (input && input.dataset.origName) {
          input.name = input.dataset.origName;
          delete input.dataset.origName;
        }
      };

      this.applyQuantity = () => {
        if (!this.mobileQuery.matches) {
          restore();
          return;
        }
        if (wrap) return;

        const picker = this.productSection.querySelector('variant-picker');
        const anchor = (picker && (picker.querySelector('.option-selector--size') || picker.firstElementChild)) || null;

        // No variant options (single-variant product): keep the quantity in place, hide only the button.
        if (!anchor) {
          row.classList.add('is-qty-fallback');
          return;
        }

        wrap = document.createElement('div');
        wrap.className = 'product-qty-mobile';
        const label = document.createElement('p');
        label.className = 'label product-qty-mobile__label';
        label.textContent = this.dataset.qtyLabel || 'Quantity';
        wrap.appendChild(label);
        wrap.appendChild(qtyEl);

        // Keep quantity changes from being mistaken for a size/colour change by the variant picker.
        const onQtyEvent = (evt) => {
          this.syncQuantity();
          if (evt.type === 'change') evt.stopPropagation();
        };
        wrap.addEventListener('change', onQtyEvent);
        wrap.addEventListener('input', onQtyEvent);
        wrap.addEventListener('click', () => setTimeout(() => this.syncQuantity(), 0));
        wrap.addEventListener('focusout', () => this.syncQuantity());

        anchor.parentNode.insertBefore(wrap, anchor);

        // The form gets its quantity from a hidden field; the visible input no longer submits.
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        row.appendChild(hidden);
        if (input) {
          input.dataset.origName = input.name || 'quantity';
          input.removeAttribute('name');
        }
        this.syncQuantity();
      };

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.applyQuantity);
      }
      this.applyQuantity();
    }

    updateSpacing() {
      const active = this.mobileQuery && this.mobileQuery.matches;
      document.body.classList.toggle('has-sticky-atc', !!active);
      document.documentElement.style.setProperty('--sticky-atc-height', active ? `${this.offsetHeight}px` : '0px');
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.magicObserver) this.magicObserver.disconnect();
      document.body.classList.remove('has-sticky-atc');
      window.removeEventListener('scroll', this.handleScroll);
      if (this.observer) this.observer.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
    }

    bindEvents() {
      // Mobile: the bar is always visible (like Lavie). Large screens: it only appears
      // once the real "Add to cart" button has scrolled out of view.
      this.mobileQuery = window.matchMedia('(max-width: 768.98px)');
      this.mainButtonVisible = true;
      this.updateVisibility = () => {
        const hidden = !this.mobileQuery.matches && this.mainButtonVisible;
        this.classList.toggle('sticky-atc-panel--out', hidden);
        this.updateSpacing();
      };

      const target = this.mainBtn || this.productForm;
      if ('IntersectionObserver' in window && target) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.mainButtonVisible = entry.isIntersecting;
          });
          this.updateVisibility();
        });
        this.observer.observe(target);
      }
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.updateVisibility);
      }

      // Reserve space at the bottom of the page on mobile so the bar never hides the footer.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateSpacing());
        this.resizeObserver.observe(this);
      }
      this.updateVisibility();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.btn.addEventListener('click', this.onClick.bind(this));
      if (this.quickBtn) this.quickBtn.addEventListener('click', this.onQuickCheckout.bind(this));

      // Keep price / image in sync with the selected variant.
      this.productSection.addEventListener('on:variant:change', (evt) => this.onVariantChange(evt));

      // Mirror state (disabled, loading, label) of the real button.
      if (this.mainBtn && 'MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(() => this.syncButton());
        this.mutationObserver.observe(this.mainBtn, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true
        });
        this.syncButton();
      }
    }

    onClick() {
      if (!this.mainBtn) return;

      // No variant chosen yet / unavailable: take the shopper to the options.
      if (this.mainBtn.disabled) {
        const anchor = document.getElementById(this.dataset.anchor);
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const form = this.mainBtn.form || this.productForm.querySelector('form');
      if (!form) return;

      if (this.syncQuantity) this.syncQuantity();

      // Reuses the theme's own submit handler (cart drawer, errors, redirects).
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(this.mainBtn);
      } else {
        this.mainBtn.click();
      }
    }

    /**
     * Clicks target: resolves shadow-DOM hosts and wrapper elements to the real button.
     */
    static resolveClickable(el) {
      if (el.shadowRoot) {
        const inner = el.shadowRoot.querySelector('button, a, [role="button"]');
        if (inner) return inner;
      }
      return el.closest('button, a, [role="button"]') || el.querySelector('button, a, [role="button"]') || el;
    }

    /**
     * Finds the on-page checkout app button (Razorpay Magic Checkout etc.).
     * Uses the optional CSS selector setting, then known hints, then the label text.
     */
    findAppCheckoutButton() {
      const outside = (el) => !this.contains(el);
      const scopes = [this.productSection, document];

      const custom = this.quickBtn.dataset.appSelector;
      if (custom) {
        try {
          const el = Array.from(document.querySelectorAll(custom)).find(outside);
          if (el) return StickyAtcPanel.resolveClickable(el);
        } catch (e) {
          /* invalid selector - fall through to auto-detect */
        }
      }

      const hints =
        '[id*="razorpay" i],[class*="razorpay" i],[id*="rzp" i],[class*="rzp" i],' +
        '[id*="magic" i][id*="checkout" i],[class*="magic" i][class*="checkout" i]';
      for (let i = 0; i < scopes.length; i += 1) {
        const hit = Array.from(scopes[i].querySelectorAll(hints)).find(
          (el) => outside(el) && !el.closest('script, style, head')
        );
        if (hit) return StickyAtcPanel.resolveClickable(hit);
      }

      for (let i = 0; i < scopes.length; i += 1) {
        const matches = Array.from(scopes[i].querySelectorAll('button, a, [role="button"], div, span'))
          .filter((el) => {
            const text = (el.textContent || '').trim();
            return outside(el) && text.length < 60 && /quick\s*checkout/i.test(text);
          })
          .sort((a, b) => a.textContent.length - b.textContent.length);
        if (matches.length) return StickyAtcPanel.resolveClickable(matches[0]);
      }

      return null;
    }

    /**
     * Fallback: adds the selected variant to the cart, then goes straight to checkout.
     */
    async onQuickCheckout() {
      if (!this.mainBtn) return;

      if (this.mainBtn.disabled) {
        const anchor = document.getElementById(this.dataset.anchor);
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (this.quickBtn.classList.contains('is-loading')) return;

      // Preferred: trigger the checkout app's own button (Razorpay etc.) so the
      // exact same flow runs as the on-page "Quick Checkout" button.
      const appBtn = this.findAppCheckoutButton();
      if (appBtn) {
        appBtn.click();
        return;
      }

      const form = this.mainBtn.form || this.productForm.querySelector('form');
      if (!form) return;

      if (this.syncQuantity) this.syncQuantity();
      this.quickBtn.classList.add('is-loading');
      this.quickBtn.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || response.status);
        }
        window.location.href = this.quickBtn.dataset.checkoutUrl || '/checkout';
      } catch (err) {
        this.quickBtn.classList.remove('is-loading');
        this.quickBtn.setAttribute('aria-disabled', 'false');
        const errEl = form.querySelector('.js-form-error');
        if (errEl && err.message) {
          errEl.textContent = err.message;
          errEl.hidden = false;
        }
        const anchor = document.getElementById(this.dataset.anchor);
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    syncButton() {
      const main = this.mainBtn;
      const loading = main.classList.contains('is-loading');
      const disabled = main.disabled;
      const text = main.textContent.trim();

      this.btn.classList.toggle('is-loading', loading);
      this.btn.setAttribute('aria-disabled', main.getAttribute('aria-disabled') === 'true' ? 'true' : 'false');
      this.btn.classList.toggle('is-disabled', disabled);
      if (this.btnLabel && text && !loading) this.btnLabel.textContent = text;
    }

    onVariantChange(evt) {
      const { variant } = evt.detail || {};
      if (!variant) return;

      // Keep the price shown in the bar in sync with the on-page price.
      const pagePrice = this.productSection.querySelector('.product-info__price .price');
      const barPrice = this.querySelector('[data-sticky-price]');
      if (pagePrice && barPrice) {
        requestAnimationFrame(() => {
          const fresh = this.productSection.querySelector('.product-info__price .price');
          if (fresh) barPrice.innerHTML = fresh.outerHTML;
        });
      }

      const media = variant.featured_media && variant.featured_media.preview_image;
      if (this.imgEl && media && media.src) {
        const src = media.src.replace(/(\.[a-z]+)(\?|$)/i, '_96x$1$2');
        this.imgEl.src = src;
        this.imgEl.removeAttribute('srcset');
      }
    }

    /**
     * Hides the bar when the page is scrolled to the very bottom (footer).
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}